import React, { useMemo, useState, useCallback } from 'react';
import { Phone, MessageCircle, PlusCircle, Info, Calendar } from 'lucide-react';
import isMobileDevice from '../../utils/platform';
import getWhatsAppUrl from '../../utils/whatsapp';
import CallButtonMobile from '../mobile/CallButtonMobile';
import eventBus from '../../utils/eventBus'; // Task 34
import toast from '../../utils/toast'; // Task 34

const QuickActionBar = ({ candidate, recruiter = null, jd = null, onAddLog = null, onAddFollowUp = null, className = '' }) => {
  const isMobile = useMemo(() => isMobileDevice(), []);
  const [tooltip, setTooltip] = useState(false);

  const handleWhatsApp = useCallback(async () => {
    if (!candidate?.phone) {
      toast.warning('No phone number available for WhatsApp');
      return;
    }
    
    try {
      const url = getWhatsAppUrl(candidate.phone);
      const target = isMobile ? '_self' : '_blank';
      window.open(url, target);
      
      // Task 34: Emit analytics event
      await eventBus.emit('whatsapp_sent', {
        candidate_id: candidate.id,
        jd_id: jd?.id,
        source: 'QuickActionBar',
        metadata: {
          phone: candidate.phone,
          candidate_name: candidate.name
        }
      });
      
      toast.success('WhatsApp opened successfully');
    } catch (e) {
      toast.error('Failed to open WhatsApp');
      console.error('WhatsApp error:', e);
    }
  }, [candidate, jd, isMobile]);

  const handleAddLog = useCallback(() => {
    if (onAddLog) {
      onAddLog(candidate, jd);
      // Event will be emitted when modal completes
    }
  }, [onAddLog, candidate, jd]);

  const handleAddFollowUp = useCallback(() => {
    if (onAddFollowUp) {
      onAddFollowUp(candidate);
    }
  }, [onAddFollowUp, candidate]);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {isMobile ? (
        <CallButtonMobile candidate={candidate} jdId={jd?.id ?? null} className="!w-12 !h-12" />
      ) : (
        <div className="relative">
          <button
            className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
            onClick={() => {
              setTooltip(true);
              window.setTimeout(() => setTooltip(false), 3000);
            }}
            aria-label="Call (mobile only)"
          >
            <Phone className="w-5 h-5" />
          </button>
          {tooltip && (
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded shadow">
              <div className="flex items-center gap-1"><Info className="w-3 h-3" /> Calling is only available from your mobile browser.</div>
            </div>
          )}
        </div>
      )}

      <button
        className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-colors"
        onClick={handleWhatsApp}
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-5 h-5" />
      </button>

      <button
        className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition-colors"
        onClick={handleAddLog}
        aria-label="Add Call Log"
      >
        <PlusCircle className="w-5 h-5" />
      </button>

      {onAddFollowUp && (
        <button
          className="w-12 h-12 rounded-full bg-teal text-white flex items-center justify-center hover:bg-teal-dark transition-colors"
          onClick={handleAddFollowUp}
          aria-label="Add Follow-Up"
        >
          <Calendar className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default React.memo(QuickActionBar);
